import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ClipboardCheck, Loader2, ArrowLeft, MailCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSubmitted(true);
      toast.success(res.data?.message || 'Reset link sent if the email exists');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send reset link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center mb-4 shadow-lg">
            <ClipboardCheck className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-800 tracking-tight">QA BUILDER PRO</h1>
        </div>

        <Card className="border-slate-200 shadow-xl bg-white">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="font-heading text-2xl tracking-tight" data-testid="forgot-password-title">RESET PASSWORD</CardTitle>
            <CardDescription className="text-slate-500">
              Enter your account email and we'll send you a link to reset your password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div
                className="flex flex-col items-center text-center py-4 space-y-4"
                data-testid="forgot-password-success"
              >
                <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
                  <MailCheck className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 mb-1">Check your inbox</h3>
                  <p className="text-sm text-slate-500">
                    If an account exists for <span className="font-medium text-slate-700">{email}</span>, we've sent
                    a password reset link. The link will expire in 1 hour.
                  </p>
                </div>
                <Link
                  to="/login"
                  className="text-sm text-primary font-medium hover:text-primary/80"
                  data-testid="back-to-login-link"
                >
                  ← Back to sign in
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11 border-slate-200 focus:border-primary focus:ring-primary"
                    data-testid="forgot-password-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-primary hover:bg-primary/90 font-semibold shadow-md hover:shadow-lg transition-all active:scale-[0.98]"
                  disabled={isLoading || !email}
                  data-testid="forgot-password-submit-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending link...
                    </>
                  ) : (
                    'Send reset link'
                  )}
                </Button>
                <Link
                  to="/login"
                  className="flex items-center justify-center gap-1 text-sm text-slate-500 hover:text-slate-700 pt-2"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to sign in
                </Link>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-6">© 2025 QA Builder Pro</p>
      </div>
    </div>
  );
}
